// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';

import { TranslateModule } from '@ngx-translate/core';

import { CtsGantryStartupActionsComponent } from '../cts-gantry-startup-actions/cts-gantry-startup-actions.component';
import { CtsGantryInitEventEType } from '../../enum/cts-gantry-init-event-type';
import { CtsGantryFunctionalUnitStepLabelsComponent } from '../cts-gantry-functional-unit-step-labels/cts-gantry-functional-unit-step-labels.component';
import { CtsGantryFunctionalUnitListComponent } from '../cts-gantry-functional-unit-list/cts-gantry-functional-unit-list.component';
import { CtsGantryFunctionalUnitGridComponent } from '../cts-gantry-functional-unit-grid/cts-gantry-functional-unit-grid.component';

import { CtsGantryEventSelectionModel } from '../../model/cts-gantry-event-selection-model';
import { CtsGantryInitEvent } from '../../interfaces/cts-gantry-init-event';
import { CtsGantryFunctionalUnitGridLegendComponent } from '../cts-gantry-functional-unit-grid-legend/cts-gantry-functional-unit-grid-legend.component';
import { CtsGantryGridSourceEType } from '../../enum/cts-gantry-grid-source-type';

import { CtstatusService } from '../../services/ctstatus.service';
import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { APICallGateComponent } from 'src/app/modules/shared/components/api-call-gate.component';
import { CtsGantryFunctionalUnitStep } from '../../interfaces/cts-gantry-functional-unit-step';
import { Subject, takeUntil } from 'rxjs';
import { CtsGantryFunctionalUnitData } from '../../interfaces/cts-gantry-functional-unit-data';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    CtsGantryStartupActionsComponent,
    CtsGantryFunctionalUnitListComponent,
    CtsGantryFunctionalUnitGridComponent,
    CtsGantryFunctionalUnitStepLabelsComponent,
    CtsGantryFunctionalUnitGridLegendComponent,
    APICallGateComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-cts-gantry-startup-pane',
  templateUrl: './cts-gantry-startup-pane.component.html',
  styleUrl: './cts-gantry-startup-pane.component.scss',
})
export class CtsGantryStartupPaneComponent
  implements OnInit, OnChanges, OnDestroy
{
  @Output() detailEventChange = new EventEmitter<CtsGantryInitEvent>();
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @Input({ required: true }) selectionModel =
    new CtsGantryEventSelectionModel();
  @Input({ required: true }) events: CtsGantryInitEvent[] = [];

  protected gantryworkflow1st = this.ctsystemService.gantryworkflow1st;
  protected gantryworkflow2nd = this.ctsystemService.gantryworkflow2nd;
  protected gantryworkflowDetail = this.ctsystemService.gantryworkflowDetail;

  protected readonly FIRST = CtsGantryGridSourceEType.FIRST;
  protected readonly SECOND = CtsGantryGridSourceEType.SECOND;

  private readonly unsubscribe = new Subject<void>();

  public constructor(protected readonly ctsystemService: CtstatusService) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['events'].currentValue) {
      this.initSelectionModel();
    }
  }

  public ngOnInit(): void {
    this.listenToFunctionalUnitData(
      this.gantryworkflow1st,
      this.selectionModel.update1stFunctionalData,
    );
    this.listenToFunctionalUnitData(
      this.gantryworkflow2nd,
      this.selectionModel.update2ndFunctionalData,
    );
    this.listenToFunctionalUnitData(
      this.gantryworkflowDetail,
      this.selectionModel.updateDetailsFunctionalData,
    );
  }

  public ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  private initSelectionModel(): void {
    if (this.events.length > 0) {
      const errorEvents = this.filterEvents([
        CtsGantryInitEventEType.UNKNOWN,
        CtsGantryInitEventEType.AUTOREBOOT,
        CtsGantryInitEventEType.COMPLETED_WITH_ERROR,
        CtsGantryInitEventEType.INCOMPLETE,
      ]);
      const successEvents = this.filterEvents(CtsGantryInitEventEType.STANDBY);

      let first: CtsGantryInitEvent | undefined;
      let second: CtsGantryInitEvent | undefined;
      if (this.events.length > 1) {
        if (errorEvents.length > 0) {
          first = errorEvents[0];
          second = errorEvents[1];
          second ??= successEvents[0];
        } else {
          first = this.events[0];
          second = this.events[1];
        }
      } else if (this.events.length === 1) {
        first = this.events[0];
        second = this.events[0];
      }

      if (first) {
        this.openLogs(first, this.FIRST)();
      }

      if (second) {
        this.on2ndSelectedEventChange(second);
      }
    }
  }

  private filterEvents(
    eventTypes: CtsGantryInitEventEType | CtsGantryInitEventEType[],
  ): CtsGantryInitEvent[] {
    const types = Array.isArray(eventTypes) ? eventTypes : [eventTypes];
    let results: CtsGantryInitEvent[] = [];
    if (this.events.length > 0) {
      results = this.events.filter((event) => types.includes(event.type));
    }

    return results;
  }

  protected readonly openLogs: (
    ctsEvent: CtsGantryInitEvent,
    gridSource: CtsGantryGridSourceEType,
  ) => () => void =
    (ctsEvent: CtsGantryInitEvent, gridSource: CtsGantryGridSourceEType) =>
    () => {
      this.selectionModel.detailSource = gridSource;
      this.onDetailSelectedEventChange(ctsEvent);
    };

  public readonly openCompare: () => void = () => {
    this.selectionModel.detailEvent = undefined;
    this.selectionModel.detailSource = undefined;
    this.selectionModel.detailFUnitData = undefined;
    this.detailEventChange.emit(this.selectionModel.detailEvent);
    this.triggerLastSuccessfulData();
  };

  private triggerLastSuccessfulData(): void {
    const fUnitData = this.selectionModel.fUnitData;
    const first = fUnitData.getFirst();
    if (first) {
      this.gantryworkflow1st.lastSuccessfulData$.next(first);
    }

    const second = fUnitData.getSecond();
    if (second) {
      this.gantryworkflow2nd.lastSuccessfulData$.next(second);
    }
  }

  protected on1stSelectedEventChange(selectedEvent: CtsGantryInitEvent): void {
    this.selectionModel.ctsEvent.first = selectedEvent;
    this.requestFunctionalUnits(
      this.gantryworkflow1st,
      selectedEvent.eventSource,
      this.update1stFUnitData,
    );
  }

  private readonly update1stFUnitData = (data: CtsGantryFunctionalUnitData) => {
    this.selectionModel.fUnitData.updateFirst(data);
  };

  protected on2ndSelectedEventChange(selectedEvent: CtsGantryInitEvent): void {
    this.selectionModel.ctsEvent.second = selectedEvent;
    this.requestFunctionalUnits(
      this.gantryworkflow2nd,
      selectedEvent.eventSource,
      this.update2ndFUnitData,
    );
  }

  private readonly update2ndFUnitData = (data: CtsGantryFunctionalUnitData) => {
    this.selectionModel.fUnitData.updateSecond(data);
  };

  protected onDetailSelectedEventChange(
    selectedEvent: CtsGantryInitEvent,
  ): void {
    this.selectionModel.updateDetailEvent(selectedEvent);
    this.detailEventChange.emit(this.selectionModel.detailEvent);
    this.requestFunctionalUnits(
      this.gantryworkflowDetail,
      selectedEvent.eventSource,
      this.updateDetailFUnitData,
    );
  }

  private readonly updateDetailFUnitData = (
    data: CtsGantryFunctionalUnitData,
  ) => {
    this.selectionModel.updateDetailFunctionalUnitData(data);
  };

  private requestFunctionalUnits(
    apiCall: typeof this.gantryworkflow1st,
    eventSource: string,
    updateFunctionalUnitDataFn: (data: CtsGantryFunctionalUnitData) => void,
  ): void {
    if (!this.selectionModel.hasFunctionalUnitData(eventSource)) {
      apiCall.request({
        EventSource: eventSource,
        MaterialNumber: this.systemDetails.systemMaterialNumber,
        SerialNumber: this.systemDetails.systemSerialNumber,
      });
    } else {
      const data = this.selectionModel.getCachedFunctionalUnitData(eventSource);
      if (data) {
        updateFunctionalUnitDataFn(data);
      }
    }
  }

  protected mapStepNames(steps: CtsGantryFunctionalUnitStep[]): string[] {
    return steps ? steps.map((step) => step.name) : [];
  }

  private listenToFunctionalUnitData(
    apiCall: typeof this.gantryworkflow1st,
    callback: (functionalUnitData: any) => void,
  ): void {
    apiCall.lastSuccessfulData$
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(callback);
  }
}
